import { useCallback, useEffect, useRef, useState } from 'react';

// ProviderInfoTooltip v0.2.0 — Phase 2D Tier 1/2 description popup.
//
// The trigger is a small bright-yellow `?` button placed right after the
// provider's name. When opened, the panel uses `position: fixed` and explicit
// coordinates derived from the button's bounding rect, so it can never be
// clipped by an ancestor's overflow-hidden (which is what was happening in
// the IssuesCard left-column case). The position is also clamped to the
// viewport so the panel never spills off-screen on either side.
interface Props {
  description: string;
}

const PANEL_WIDTH = 288; // px — must match the w-72 below

export default function ProviderInfoTooltip({ description }: Props) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<{ top: number; left: number }>({ top: 0, left: 0 });
  const buttonRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  // Recompute fixed position from the button's bounding rect, clamped so the
  // panel sits inside the viewport with an 8px gutter.
  // useCallback so the effect's dependency array can include reposition
  // without re-running the effect on every render (which would re-attach
  // listeners). The closure captures only refs + setPos which are stable.
  const reposition = useCallback((): void => {
    const btn = buttonRef.current;
    if (!btn) return;
    const rect = btn.getBoundingClientRect();
    const top = rect.bottom + 6;
    const ideal = rect.left;
    const maxLeft = window.innerWidth - PANEL_WIDTH - 8;
    const left = Math.max(8, Math.min(ideal, maxLeft));
    setPos({ top, left });
  }, []);

  useEffect(() => {
    if (!open) return;
    reposition();
    const closer = (e: MouseEvent) => {
      const t = e.target as Node;
      if (buttonRef.current?.contains(t) || panelRef.current?.contains(t)) {
        return;
      }
      setOpen(false);
    };
    const onScrollOrResize = () => reposition();
    document.addEventListener('mousedown', closer);
    window.addEventListener('scroll', onScrollOrResize, true);
    window.addEventListener('resize', onScrollOrResize);
    return () => {
      document.removeEventListener('mousedown', closer);
      window.removeEventListener('scroll', onScrollOrResize, true);
      window.removeEventListener('resize', onScrollOrResize);
    };
  }, [open, reposition]);

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        onClick={(e) => {
          // Prevent parent row/card click handlers from firing.
          e.stopPropagation();
          setOpen((v) => !v);
        }}
        aria-label="Provider description"
        aria-expanded={open}
        className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-border text-warn hover:bg-surface2 text-[11px] leading-none font-bold transition-colors shrink-0"
      >
        ?
      </button>
      {open && (
        <div
          ref={panelRef}
          style={{
            position: 'fixed',
            top: pos.top,
            left: pos.left,
            width: PANEL_WIDTH,
            zIndex: 60,
          }}
          className="bg-surface border border-border rounded-md shadow-lg p-3 text-xs leading-relaxed text-text"
        >
          {description}
        </div>
      )}
    </>
  );
}
